import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import OTPInput from 'react-otp-input';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API from '../services/api';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const toastConfig = {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light'
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match", toastConfig);
      return;
    }
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/register`, { name, mobile, email, password });
      setOtpSent(true);
    } catch (error) {
      console.error('Signup failed', error);
      toast.error(error.response.data.message, toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/verify-otp`, { email, otp });
      setIsVerified(true);
      navigate('/login');
    } catch (error) {
      console.error('OTP verification failed', error);
      toast.error("Wrong OTP. Please try again.", toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.08 } },
    exit: { opacity: 0, transition: { duration: 0.4, ease: "easeIn" } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } }
  };

  const sharedStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

    .auth-root {
      min-height: 100vh;
      background: #0a0a0f;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Poppins', sans-serif;
      position: relative;
      overflow: hidden;
    }
    .blob { position: absolute; border-radius: 50%; filter: blur(90px); opacity: 0.18; pointer-events: none; }
    .blob-1 { width: 420px; height: 420px; background: radial-gradient(circle, #6c63ff, #a855f7); top: -80px; left: -100px; animation: drift1 10s ease-in-out infinite alternate; }
    .blob-2 { width: 340px; height: 340px; background: radial-gradient(circle, #06b6d4, #3b82f6); bottom: -60px; right: -80px; animation: drift2 12s ease-in-out infinite alternate; }
    .blob-3 { width: 200px; height: 200px; background: radial-gradient(circle, #f472b6, #ec4899); top: 50%; left: 60%; animation: drift3 8s ease-in-out infinite alternate; }
    @keyframes drift1 { from { transform: translate(0,0) scale(1); } to { transform: translate(40px,30px) scale(1.08); } }
    @keyframes drift2 { from { transform: translate(0,0) scale(1); } to { transform: translate(-30px,-40px) scale(1.05); } }
    @keyframes drift3 { from { transform: translate(0,0) scale(1); } to { transform: translate(-20px,20px) scale(0.9); } }
    .grid-overlay {
      position: absolute; inset: 0;
      background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
      background-size: 40px 40px; pointer-events: none;
    }
    .auth-card {
      position: relative; z-index: 10; width: 100%; max-width: 440px; margin: 24px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 24px; padding: 44px 44px;
      backdrop-filter: blur(20px);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(108,99,255,0.06);
    }
    .auth-brand { text-align: center; margin-bottom: 32px; }
    .auth-logo {
      display: inline-flex; align-items: center; justify-content: center;
      width: 52px; height: 52px; border-radius: 14px;
      background: linear-gradient(135deg, #6c63ff, #a855f7);
      margin-bottom: 18px;
      box-shadow: 0 8px 24px rgba(108,99,255,0.35);
    }
    .auth-title {
      font-family: 'Poppins', sans-serif;
      font-size: 2rem; font-weight: 500; color: #f0f0f8;
      letter-spacing: -0.02em; line-height: 1.1; margin: 0 0 6px;
    }
    .auth-subtitle { font-size: 0.83rem; color: rgba(255,255,255,0.6); font-weight: 300; letter-spacing: 0.02em; }
    .divider { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
    .divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.07); }
    .divider-text { font-size: 0.7rem; color: rgba(255,255,255,0.46); letter-spacing: 0.12em; text-transform: uppercase; }
    .field-group { margin-bottom: 14px; position: relative; }
    .field-label { display: block; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.56); margin-bottom: 7px; transition: color 0.2s; }
    .field-group.focused .field-label { color: #a78bfa; }
    .field-wrapper { position: relative; }
    .field-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); width: 15px; height: 15px; color: rgba(255,255,255,0.46); transition: color 0.2s; pointer-events: none; }
    .field-group.focused .field-icon { color: #a78bfa; }
    .field-input {
      width: 100%; padding: 12px 14px 12px 40px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 12px; color: #f0f0f8;
      font-size: 0.88rem; font-family: 'Poppins', sans-serif;
      outline: none; transition: all 0.25s ease; box-sizing: border-box;
    }
    .field-input::placeholder { color: rgba(255,255,255,0.4); }
    .field-input:focus {
      background: rgba(167,139,250,0.06);
      border-color: rgba(167,139,250,0.4);
      box-shadow: 0 0 0 3px rgba(167,139,250,0.08);
    }
    .field-input.has-btn { padding-right: 70px; }
    .eye-btn {
      position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
      background: none; border: none; cursor: pointer;
      font-size: 0.7rem; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase;
      color: rgba(255,255,255,0.52); font-family: 'Poppins', sans-serif;
      transition: color 0.2s; padding: 4px 6px; border-radius: 6px;
    }
    .eye-btn:hover { color: #a78bfa; background: rgba(167,139,250,0.1); }
    .submit-btn {
      width: 100%; padding: 14px; border-radius: 12px; border: none;
      cursor: pointer; font-family: 'Poppins', sans-serif;
      font-size: 0.9rem; font-weight: 500; letter-spacing: 0.04em;
      color: white;
      background: linear-gradient(135deg, #6c63ff 0%, #a855f7 100%);
      position: relative; overflow: hidden; transition: all 0.3s ease;
      box-shadow: 0 4px 20px rgba(108,99,255,0.3);
      display: flex; align-items: center; justify-content: center; gap: 8px;
      margin-top: 20px;
    }
    .submit-btn::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.12), transparent); opacity: 0; transition: opacity 0.3s; }
    .submit-btn:hover:not(:disabled)::before { opacity: 1; }
    .submit-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(108,99,255,0.45); }
    .submit-btn:active:not(:disabled) { transform: translateY(0); }
    .submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }
    .spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.7s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .bottom-row { text-align: center; margin-top: 22px; font-size: 0.82rem; color: rgba(255,255,255,0.52); }
    .bottom-row a { color: #a78bfa; text-decoration: none; font-weight: 500; transition: color 0.2s; }
    .bottom-row a:hover { color: #c4b5fd; }

    /* OTP styles */
    .otp-info { text-align: center; margin-bottom: 28px; }
    .otp-email-badge {
      display: inline-block; margin-top: 8px; padding: 4px 14px;
      background: rgba(167,139,250,0.1); border: 1px solid rgba(167,139,250,0.2);
      border-radius: 20px; font-size: 0.82rem; color: #c4b5fd; font-weight: 500;
    }
    .otp-info p { font-size: 0.83rem; color: rgba(255,255,255,0.6); }
    .otp-container { display: flex !important; justify-content: center; gap: 10px; margin-bottom: 8px; }
    .otp-input-box {
      width: 48px !important; height: 54px !important;
      background: rgba(255,255,255,0.04) !important;
      border: 1px solid rgba(255,255,255,0.1) !important;
      border-radius: 12px !important;
      color: #f0f0f8 !important; font-size: 1.3rem !important;
      font-family: 'Poppins', sans-serif !important; font-weight: 600 !important;
      text-align: center; outline: none !important;
      transition: all 0.2s ease !important;
      caret-color: #a78bfa;
    }
    .otp-input-box:focus {
      background: rgba(167,139,250,0.07) !important;
      border-color: rgba(167,139,250,0.45) !important;
      box-shadow: 0 0 0 3px rgba(167,139,250,0.1) !important;
    }
    .step-dots { display: flex; justify-content: center; gap: 6px; margin-bottom: 28px; }
    .step-dot { width: 28px; height: 3px; border-radius: 2px; transition: all 0.3s ease; }
    .step-dot.active { background: linear-gradient(90deg, #6c63ff, #a855f7); }
    .step-dot.inactive { background: rgba(255,255,255,0.1); }

    @media (max-width: 480px) {
      .auth-card { padding: 28px 20px; margin: 16px; }
      .auth-title { font-size: 1.7rem; }
      .otp-container { gap: 7px !important; }
      .otp-input-box { width: 38px !important; height: 46px !important; font-size: 1.1rem !important; }
    }
  `;

  /* ─────────── OTP Screen ─────────── */
  if (otpSent && !isVerified) {
    return (
      <>
        <style>{sharedStyles}</style>
        <motion.div className="auth-root" variants={containerVariants} initial="hidden" animate="visible" exit="exit">
          <div className="blob blob-1" /><div className="blob blob-2" /><div className="blob blob-3" />
          <div className="grid-overlay" />

          <motion.div className="auth-card" variants={itemVariants}>
            <motion.div className="auth-brand" variants={itemVariants}>
              <div className="auth-logo">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
                </svg>
              </div>
              <h1 className="auth-title">Verify your email</h1>
              <p className="auth-subtitle">Enter the 6-digit code we sent you</p>
            </motion.div>

            <motion.div className="step-dots" variants={itemVariants}>
              <div className="step-dot inactive" />
              <div className="step-dot active" style={{width:'44px'}} />
            </motion.div>

            <motion.div className="otp-info" variants={itemVariants}>
              <p style={{color:'rgba(255,255,255,0.6)', fontSize:'0.82rem'}}>Code sent to</p>
              <span className="otp-email-badge">{email}</span>
            </motion.div>

            <form onSubmit={handleOtpSubmit}>
              <motion.div variants={itemVariants}>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  renderInput={(props) => (
                    <input {...props} className="otp-input-box" />
                  )}
                  containerStyle="otp-container"
                />
              </motion.div>

              <motion.button type="submit" className="submit-btn" disabled={loading} variants={itemVariants} whileTap={{ scale: 0.98 }}>
                {loading ? (
                  <><div className="spinner" /><span>Verifying...</span></>
                ) : (
                  <>
                    <span>Verify & Continue</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </>
                )}
              </motion.button>

              <motion.div className="bottom-row" variants={itemVariants}>
                Wrong email? <Link to="/signup" onClick={() => setOtpSent(false)}>Go back</Link>
              </motion.div>
            </form>
          </motion.div>
        </motion.div>
        <ToastContainer />
      </>
    );
  }

  /* ─────────── Signup Screen ─────────── */
  return (
    <>
      <style>{sharedStyles}</style>
      <motion.div className="auth-root" variants={containerVariants} initial="hidden" animate="visible" exit="exit">
        <div className="blob blob-1" /><div className="blob blob-2" /><div className="blob blob-3" />
        <div className="grid-overlay" />

        <motion.div className="auth-card" variants={itemVariants}>

          <motion.div className="auth-brand" variants={itemVariants}>
            <div className="auth-logo">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h1 className="auth-title">Create account</h1>
            <p className="auth-subtitle">Join us — it only takes a moment</p>
          </motion.div>

          <motion.div className="step-dots" variants={itemVariants}>
            <div className="step-dot active" style={{width:'44px'}} />
            <div className="step-dot inactive" />
          </motion.div>

          <motion.div className="divider" variants={itemVariants}>
            <div className="divider-line" />
            <span className="divider-text">your details</span>
            <div className="divider-line" />
          </motion.div>

          <form onSubmit={handleSignupSubmit}>
            {/* Name */}
            <motion.div className={`field-group ${focusedField === 'name' ? 'focused' : ''}`} variants={itemVariants}>
              <label className="field-label">Full name</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <input type="text" placeholder="Jane Doe" value={name} onChange={(e) => setName(e.target.value)}
                  onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)}
                  className="field-input" required />
              </div>
            </motion.div>

            {/* Mobile */}
            <motion.div className={`field-group ${focusedField === 'mobile' ? 'focused' : ''}`} variants={itemVariants}>
              <label className="field-label">Mobile number</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                <input type="text" placeholder="+91 98765 43210" value={mobile} onChange={(e) => setMobile(e.target.value)}
                  onFocus={() => setFocusedField('mobile')} onBlur={() => setFocusedField(null)}
                  className="field-input" required />
              </div>
            </motion.div>

            {/* Email */}
            <motion.div className={`field-group ${focusedField === 'email' ? 'focused' : ''}`} variants={itemVariants}>
              <label className="field-label">Email address</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
                <input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                  className="field-input" required />
              </div>
            </motion.div>

            {/* Password */}
            <motion.div className={`field-group ${focusedField === 'password' ? 'focused' : ''}`} variants={itemVariants}>
              <label className="field-label">Password</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')} onBlur={() => setFocusedField(null)}
                  className="field-input has-btn" required />
                <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </motion.div>

            {/* Confirm Password */}
            <motion.div className={`field-group ${focusedField === 'confirm' ? 'focused' : ''}`} variants={itemVariants}>
              <label className="field-label">Confirm password</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => setFocusedField('confirm')} onBlur={() => setFocusedField(null)}
                  className="field-input" required />
              </div>
            </motion.div>

            <motion.button type="submit" className="submit-btn" disabled={loading} variants={itemVariants} whileTap={{ scale: 0.98 }}>
              {loading ? (
                <><div className="spinner" /><span>Creating account...</span></>
              ) : (
                <>
                  <span>Create account</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </motion.button>

            <motion.div className="bottom-row" variants={itemVariants}>
              Already have an account?{' '}<Link to="/login">Sign in</Link>
            </motion.div>
          </form>
        </motion.div>
      </motion.div>
      <ToastContainer />
    </>
  );
};

export default Signup;